import React, { useState, useEffect } from 'react';
import { ConfigGenerator } from '../components/config';
import { usePlanningStore } from '../stores/planningStore';
import { Connection, MCPConfigTemplate, GeneratorOptions } from '../types';

export function Configuration() {
  const { currentPlan } = usePlanningStore();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [savedConfigs, setSavedConfigs] = useState<Array<{
    id: string;
    name: string;
    config: MCPConfigTemplate;
    options: GeneratorOptions;
    createdAt: string;
  }>>([]);

  // Load mock connections for demo
  useEffect(() => {
    if (currentPlan && currentPlan.apps.length > 0) {
      const mockConnections: Connection[] = currentPlan.apps.map((planApp, index) => ({
        id: `conn_${index}`,
        userId: 'user_1',
        profileId: 'profile_1',
        appId: planApp.app.id,
        appSlug: planApp.app.pipedreamAppId,
        accountName: `Account ${index + 1}`,
        pipedreamAccountId: `pd_acc_${index}`,
        status: 'active',
        createdAt: new Date().toISOString(),
        lastUsed: new Date().toISOString(),
      }));
      setConnections(mockConnections);
    }
  }, [currentPlan]);

  const handleSaveConfig = (config: MCPConfigTemplate, options: GeneratorOptions) => {
    const newConfig = {
      id: `config_${Date.now()}`,
      name: `${currentPlan?.name || 'Untitled'} Configuration`,
      config,
      options,
      createdAt: new Date().toISOString(),
    };

    setSavedConfigs(prev => [newConfig, ...prev]);
  };

  const deleteSavedConfig = (configId: string) => {
    setSavedConfigs(prev => prev.filter(config => config.id !== configId));
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          MCP Configuration
        </h1>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
          Generate and manage MCP server configurations for your connection plans
        </p>
      </div>

      {/* Current Plan Info */}
      {currentPlan ? (
        <div className="p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="font-medium text-sm sm:text-base text-blue-900 dark:text-blue-100 mb-2">
            Current Plan: {currentPlan.name}
          </h3>
          <div className="text-xs sm:text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <p>• {currentPlan.apps.length} apps configured</p>
            <p>• {currentPlan.connections.length} connections planned</p>
            <p>• Status: {currentPlan.status || 'draft'}</p>
            {currentPlan.description && (
              <p className="line-clamp-2">• Description: {currentPlan.description}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="p-3 sm:p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <h3 className="font-medium text-sm sm:text-base text-yellow-900 dark:text-yellow-100 mb-2">
            No Active Plan
          </h3>
          <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-200">
            Create a connection plan in the Planning workspace to generate configurations.
          </p>
        </div>
      )}

      {/* Configuration Generator */}
      <ConfigGenerator
        plan={currentPlan}
        connections={connections}
        onSave={handleSaveConfig}
      />

      {/* Saved Configurations */}
      {savedConfigs.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
            Saved Configurations ({savedConfigs.length})
          </h3>
          
          <div className="space-y-3">
            {savedConfigs.map((savedConfig) => (
              <div
                key={savedConfig.id}
                className="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-2 sm:space-y-0">
                  <div className="min-w-0">
                    <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white truncate">
                      {savedConfig.name}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Created: {new Date(savedConfig.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className="text-[10px] sm:text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                      {savedConfig.options.templateType}
                    </span>
                    <span className="text-[10px] sm:text-xs bg-blue-100 dark:bg-blue-700 text-blue-600 dark:text-blue-400 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                      {savedConfig.options.clientType}
                    </span>
                    <button
                      onClick={() => deleteSavedConfig(savedConfig.id)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      title="Delete configuration"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Servers:</span>
                    <span className="font-mono">
                      {Object.keys(savedConfig.config.mcpServers).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Format:</span>
                    <span className="font-mono">{savedConfig.options.outputFormat}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Environment:</span>
                    <span className="font-mono">
                      {savedConfig.options.includeEnv ? 'included' : 'excluded'}
                    </span>
                  </div>
                </div>

                {/* Server List Preview */}
                <div className="mt-3">
                  <details className="group">
                    <summary className="text-sm text-blue-600 dark:text-blue-400 cursor-pointer hover:underline">
                      View servers ({Object.keys(savedConfig.config.mcpServers).length})
                    </summary>
                    <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs font-mono space-y-1">
                      {Object.keys(savedConfig.config.mcpServers).map((serverName) => (
                        <div key={serverName} className="text-gray-700 dark:text-gray-300">
                          • {serverName}
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <h3 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white mb-2">
          Configuration Help
        </h3>
        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 space-y-1 sm:space-y-2">
          <p>
            <strong>Dedicated:</strong> Individual MCP servers per account (best for local development)
          </p>
          <p>
            <strong>Remote:</strong> HTTP endpoints for all connections (best for cloud deployment)
          </p>
          <p>
            <strong>Mixed:</strong> Combines both approaches for optimal performance
          </p>
          <p>
            <strong>Environment Variables:</strong> Include sensitive configuration in env vars
          </p>
        </div>
      </div>
    </div>
  );
}